/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * NEGATION AWARE KEYWORD PRESENCE, shared (v1_776). Moved from
 * api/_risk-engine.js unchanged so the case state reads "no crypto" the way the
 * risk engine does (v1_714 clause rule, v1_769 list rule). The engine re-exports
 * it; nothing about its behaviour changed in the move.
 */
// Negation-aware keyword presence (v1_714). A keyword appearing ONLY inside a
// negation — "no crypto", "not a virtual-asset business", "no digital-asset
// activity" — must not trip a risk flag: a reassurance that a business does NOT
// deal in something was flipping the product category to High off the applicant's
// own description (CompliantID Ltd, 22/09/26 — "…make outbound payments; no crypto
// or digital-asset activity…" tiered the case Low→Medium). Counts a match only
// where it is NOT preceded by a negation cue in the same clause (clause boundaries
// . ; : ,). If ANY occurrence is un-negated, the term is genuinely present.
const NEG_CUE = /\b(no|not|non|never|without|zero|nil|neither|nor|isn'?t|aren'?t|doesn'?t|don'?t|excludes?|excluding)\b[^.;:,]{0,30}$/i;
// v1_769: a negation carries across a LIST. "never holds or has access to client
// money, client assets or crypto assets" negates every item, but the clause rule
// above stops at the first comma, so "crypto" read as un-negated and rated
// product risk High (walk run 11, Low to Medium). The negation reaches a match
// when the text since the last sentence boundary is a negated head followed by
// short comma separated items, and the list ends in or / and / nor (the match's
// own item opens with the conjunction, or a later item in the same clause does).
// "no cash handling, crypto is the core product" is not a list and still fires;
// so does "no fiat services, only crypto trading" and anything after "but".
const LIST_HEAD_NEG = /\b(no|not|never|without|neither|nor|isn'?t|aren'?t|doesn'?t|don'?t|excludes?|excluding)\b/i;
const LIST_BREAK = /\b(but|however|only|except|although|though|while|whereas)\b/i;
const LIST_CONJ = /\b(or|and|nor)\b/gi;
const NOT_AN_ITEM = /^(it|we|they|he|she|who|which|that|this|there|the|its|our|their)$/i;
const wordsOf = (t) => String(t || '').trim().split(/\s+/).filter(Boolean);
function negatedByList(src, index, end, from = null, strict = false) {
  // A sentence boundary, or the quote that opens a field in the engine's JSON
  // haystack (hay() joins the fields as a JSON array, so its commas are not text).
  const start = from !== null ? from - 1 : Math.max(src.lastIndexOf('.', index - 1), src.lastIndexOf(';', index - 1), src.lastIndexOf(':', index - 1), src.lastIndexOf('"', index - 1), index - 200);
  const parts = src.slice(start + 1, index).split(',');
  if (parts.length < 2) return false;
  if (!LIST_HEAD_NEG.test(parts[0]) || LIST_BREAK.test(parts.slice(1).join(','))) return false;
  const items = parts.slice(1);
  // Every earlier item is a short noun phrase (at most six words).
  if (items.slice(0, -1).some(it => wordsOf(it).length > 6)) return false;
  // The match's own item: what follows the last conjunction in it (if any) must
  // be at most one word and not a pronoun or article opening a new clause
  // ("and it trades crypto" is a new clause, not a list item).
  const own = items[items.length - 1];
  const conj = [...own.matchAll(LIST_CONJ)].pop();
  const after = conj ? own.slice(conj.index + conj[0].length) : own;
  const lead = conj ? own.slice(0, conj.index) : '';
  if (wordsOf(after).length > 1 || (wordsOf(after)[0] && NOT_AN_ITEM.test(wordsOf(after)[0])) || wordsOf(lead).length > 6) return false;
  // From the last cue (strict): after the conjunction the match must come at
  // once ("or virtual asset"), so "have no office, and sell crypto" still fires.
  if (strict && conj && wordsOf(after).length > 0) return false;
  if (conj) return true;
  // No conjunction yet: the match is a middle item, so the list must go on to end
  // in or / and / nor within the same clause.
  const tail = src.slice(end).split(/[.;:"]/)[0];
  const t = tail.match(/^([^]*?)\b(or|and|nor)\b/i);
  if (!t || LIST_BREAK.test(t[1])) return false;
  // The rest of the match's own item is at most one word ("crypto assets"), and
  // any further items before the conjunction are short noun phrases.
  const rest = t[1].split(',');
  return wordsOf(rest[0]).length <= 1 && rest.slice(1).every(it => wordsOf(it).length <= 6);
}
// v1_910: the list may also start at the LAST negation cue before the match in
// the same sentence. Walk w5bb1930f: "accepts no cash (AML/CTF Policy Summary,
// version 3) and has no digital wallets, crypto accounts or virtual asset
// holdings of any kind" read crypto as un-negated, because the comma inside the
// bracket split the sentence so the list's first item ran to seven words and
// the list rule gave up, and product risk went High. Starting at the nearest
// "no" ("no digital wallets, crypto accounts or virtual asset holdings") the
// list is plain. Every condition of the list rule still applies (short items,
// a closing or / and / nor, no but / only / except), so "no cash handling,
// crypto is the core product" and "no fiat services, only crypto trading"
// still fire. This can only make a negated mention read as negated; it never
// makes an un-negated one disappear unless the same list rule says so.
function negatedFromLastCue(src, index, end) {
  const sentenceStart = Math.max(src.lastIndexOf('.', index - 1), src.lastIndexOf(';', index - 1), src.lastIndexOf(':', index - 1), src.lastIndexOf('"', index - 1), index - 200) + 1;
  const window = src.slice(sentenceStart, index);
  const cues = [...window.matchAll(new RegExp(LIST_HEAD_NEG.source, 'gi'))];
  if (!cues.length) return false;
  const last = cues[cues.length - 1];
  const from = sentenceStart + last.index;
  if (from === sentenceStart) return false; // the sentence rule already started here
  return negatedByList(src, index, end, from, true);
}
export const mentionsUnnegated = (text, re) => {
  const src = String(text || '');
  const g = new RegExp(re.source, 'gi');
  let m;
  while ((m = g.exec(src))) {
    if (!NEG_CUE.test(src.slice(Math.max(0, m.index - 40), m.index)) && !negatedByList(src, m.index, m.index + m[0].length) && !negatedFromLastCue(src, m.index, m.index + m[0].length)) return true;
    if (m.index === g.lastIndex) g.lastIndex++; // guard against a zero-width match
  }
  return false;
};
